import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Loading } from '@bingads-webui-campaign-react/loading';
import { Alert } from '@bingads-webui-react/alert';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ErrorMessages } from '@bingads-webui-react/validation';
import { FocusableActuator } from '@bingads-webui-campaign-react/actuator';
import { withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';
import { withFormError, withAlwaysShowError, codeIs, propertyIs, entityIs } from '@bingads-webui-campaign-react/form-error';
import { ImageListPortalBeaconAdapter } from './image-list-portal-beacon-adapter';

import { getStyles } from './style';

export const ImageListPortalContainer = (props) => {
  const {
    i18n,
    containerRef,
    containerState,
    isLogos,
    validationProps,
  } = props;

  const {
    imageAssetsLoadPending,
    imageAssetsLoadFailure,
    imageErrors,
    logoErrors,
    isDirty,
    imageListSizeChange,
  } = containerState;

  const errorMessages = isLogos ? logoErrors : imageErrors;
  const classes = useDefaultStyles(getStyles);

  const ariaAnnounceMessage = useMemo(() => {
    if (imageListSizeChange === 0) {
      return '';
    } else if (imageListSizeChange === 1) {
      return i18n.getString(_TL_('One image added'));
    } else if (imageListSizeChange === -1) {
      return i18n.getString(_TL_('One image removed'));
    } else if (imageListSizeChange > 1) {
      return i18n.getString(_TL_('{{count}} images added'), { count: imageListSizeChange });
    }

    return i18n.getString(_TL_('{{count}} images removed'), { count: Math.abs(imageListSizeChange) });
  }, [imageListSizeChange, i18n]);


  const loadingImagesSpinner = useMemo(() => (
    <div className={classes.imageListLoadingContainer}>
      <Loading text={i18n.getString(_TL_('Loading assets...'))} isVisible />
    </div>
  ), [classes.imageListLoadingContainer, i18n]);

  const loadingImageError = useMemo(() => (
    <div className={classes.imageListErrorContainer}>
      <Alert
        message={i18n.getString(_TL_('Cannot continue because image assets failed to load. Please try again or contact customer support.'))}
        level="Error"
        icon={(<div className="alert-image" />)}
        dismissible={false}
      />
    </div>
  ), [classes.imageListErrorContainer, i18n]);

  const imageListContainer = useMemo(() => (
    <div className={classes.imageExperienceContainer}>
      <div className="image-experience-content" ref={containerRef} />
      <span className={classes.ariaAnnounce} aria-live="assertive" role="alert" >{ariaAnnounceMessage}</span>
      <ErrorMessages messages={(validationProps.alwaysShowError || isDirty) && errorMessages} />
    </div>), [classes.imageExperienceContainer, classes.ariaAnnounce, containerRef, errorMessages, isDirty, validationProps.alwaysShowError, imageListSizeChange]);

  if (imageAssetsLoadPending) {
    return loadingImagesSpinner;
  }

  if (imageAssetsLoadFailure) {
    return loadingImageError;
  }

  return imageListContainer;
};

ImageListPortalContainer.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  containerRef: PropTypes.shape({}).isRequired,
  containerState: PropTypes.shape({}),
  isLogos: PropTypes.bool.isRequired,
  validationProps: PropTypes.shape({
    alwaysShowError: PropTypes.bool,
  }),
};

ImageListPortalContainer.defaultProps = {
  containerState: {},
  validationProps: {},
};

export const ImageListPortalContainerWithControlErrorHandling =
  withAlwaysShowError(withControlErrorHandling(ImageListPortalContainer, [FocusableActuator], [ImageListPortalBeaconAdapter]));

// DuplicateImage should only occure for logo (as it currently lack proper handling for adding duplicate images in some scenarios. This should not occur on image list)
export const LogoListPortalContainerWithControlErrorHandling =
  withFormError(withAlwaysShowError(withControlErrorHandling(ImageListPortalContainer, [FocusableActuator], [ImageListPortalBeaconAdapter])), { filter: entityIs('assetGroup') && propertyIs('Images') && codeIs('DuplicateImage') });
